import React from "react";
import classNames from "classnames";
import { TransactionMatch } from "@common/types/transactionMatch";
import { Transaction } from "@common/types/transaction";
import { EagerLoaded } from "@common/types/base/orm";
import { useRemoveFinancialDocumentMutation } from "@api/financialDocument.api";
import { useOverlays } from "contexts/OverlayContext";
import { useDetachTransactionMatchMutation } from "@mutations/transaction";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { DetachIcon, TrashIcon } from "@icons";
import styles from "./TransactionMatches.module.scss";

interface Props {
    transactionMatch: EagerLoaded<TransactionMatch, "financialDocument">;
    financialTransaction: Transaction;
}

export const TransactionMatchFinancialDocument: React.FC<Props> = ({
    financialTransaction,
    transactionMatch,
}) => {
    const { openOverlay } = useOverlays();
    const detachMutation = useDetachTransactionMatchMutation(
        financialTransaction.id,
        transactionMatch.id,
    );

    const removeDocumentMutation = useRemoveFinancialDocumentMutation();

    return (
        <div className={styles.match} data-testid="transaction-document">
            <Button
                data-testid="transaction-document-name"
                variant="secondary"
                size="sm"
                className={classNames("mr-auto", styles.documentName)}
                onClick={() =>
                    openOverlay(
                        "financialDocumentId",
                        transactionMatch.financialDocument.id,
                    )
                }
            >
                <span>{transactionMatch.financialDocument.fileName}</span>
            </Button>

            <ButtonWithLoader
                data-testid="detach-document-button"
                variant="secondary"
                iconOnly
                size="sm"
                aria-label="Detach from this transaction"
                onClick={() => {
                    detachMutation.mutate();
                }}
                loading={detachMutation.isPending}
                tooltip="Detach from this transaction"
            >
                <DetachIcon />
            </ButtonWithLoader>

            <ButtonWithLoader
                data-testid="remove-document-button"
                variant="secondary"
                iconOnly
                size="sm"
                aria-label="Delete receipt"
                tooltip="Delete receipt"
                loading={removeDocumentMutation.isPending}
                onClick={() => {
                    removeDocumentMutation.mutate({
                        financialDocumentId:
                            transactionMatch.financialDocument.id,
                        confirmTransactionsRemoval: true,
                    });
                }}
            >
                <TrashIcon />
            </ButtonWithLoader>
        </div>
    );
};
